import { useMemo, useState } from 'react';
import { GlobalOrdersRequest } from '@api/ringil-api.v2.tsx';
import { ExpeditionLocality, LocalityEntryModeEnum, NewExpeditionForm } from '@app/pages/expedition/create/types.ts';
import { findLoadingIndex, validateRouteAssignments } from '@app/pages/expedition/create/routeRules.ts';
import { mergeStopInto } from '@app/pages/expedition/create/stopMerge.ts';
import { Form } from '@hooks/useForm/useForm.tsx';
import { generateUUID, isNotBlank } from '@utils/utils.tsx';

/**
 * Editing operations for the route: adding and removing stops, and deciding where each order is
 * loaded and unloaded.
 *
 * Moves are taken literally — nothing is rearranged behind the user's back, even when the result
 * breaks the loading/unloading rules. What the arrangement costs is reported by
 * {@link validateRouteAssignments} and blocks submission until it is resolved.
 */
export const useRouteEditor = (form: Form<NewExpeditionForm>) => {
  /** Every order currently in an impossible position, with the reason. */
  const orderIssues = useMemo(() => validateRouteAssignments(form.values), [form.values.locations]);

  const applyStructuralChange = (nextLocations: ExpeditionLocality[]) => {
    form.overwriteValues({ ...form.values, locations: nextLocations });
  };

  /** Moves a stop to another position in the route. Assignments are left exactly as they are. */
  const moveStop = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex || toIndex < 0 || toIndex >= form.values.locations.length) return;

    const nextLocations = [...form.values.locations];
    const [moved] = nextLocations.splice(fromIndex, 1);
    nextLocations.splice(toIndex, 0, moved);
    applyStructuralChange(nextLocations);
  };

  /** Moves an order to a different loading stop, carrying its cargo with it. */
  const moveOrderLoading = (orderId: string, toLocIndex: number) => {
    const fromIndex = findLoadingIndex(form.values.locations, orderId);
    if (fromIndex < 0 || fromIndex === toLocIndex) return;

    const order = form.values.locations[fromIndex].orders.find(o => o.id === orderId);
    const nextLocations = form.values.locations.map((loc, index) => {
      if (index === fromIndex) return { ...loc, orders: loc.orders.filter(o => o.id !== orderId) };
      if (index === toLocIndex) return { ...loc, orders: [...(loc.orders || []), order] };
      return loc;
    });
    applyStructuralChange(nextLocations);
  };

  /** Inserts an empty stop at `position`, shifting the stop currently there (and everything after
   *  it) one place along. `position === locations.length` appends. */
  const insertStopAt = (position: number) => {
    const isNewLastStop = position >= form.values.locations.length;

    const newStop = {
      orders: null,
      id: generateUUID(),
      // The last stop always shows its unloading area, so a stop appended at the end starts with an
      // empty list ready to receive assignments rather than none at all.
      unloadingOrderIds: isNewLastStop ? [] : null,
      timeslots: { dayWithInterval: [] },
      localityMode: LocalityEntryModeEnum.FROM_LOCATIONS,
    } as ExpeditionLocality;

    const nextLocations = [...form.values.locations];
    nextLocations.splice(position, 0, newStop);
    applyStructuralChange(nextLocations);
  };

  /** Inserts a stop directly before `index` — the "add stop above" action on a stop. */
  const addStopAbove = (index: number) => insertStopAt(index);

  const addStopAtEnd = () => insertStopAt(form.values.locations.length);

  /** A route needs a place to load and a place to unload, so the last two stops stay. */
  const canDeleteLocation = form.values.locations.length > 2;

  const deleteLocation = (locIndex: number) => {
    if (!canDeleteLocation) return;

    const removed = form.values.locations[locIndex];
    const removedOrderIds = (removed.orders || []).map(order => order.id);

    // The orders loaded here cease to exist with the stop, so references to them are dropped
    // everywhere. Whatever this stop was unloading is *not* moved elsewhere — those orders simply
    // end up unassigned and are reported as such.
    const nextLocations = form.values.locations
      .filter((_, i) => i !== locIndex)
      .map(loc =>
        loc.unloadingOrderIds ? { ...loc, unloadingOrderIds: loc.unloadingOrderIds.filter(id => !removedOrderIds.includes(id)) } : loc,
      );

    applyStructuralChange(nextLocations);
  };

  /**
   * Folds a stop into another one at the same place, which keeps its date and position. Allowed
   * even when it breaks the loading order — {@link validateRouteAssignments} reports that.
   */
  const mergeStop = (sourceIndex: number, targetIndex: number) => {
    if (!canDeleteLocation) return;
    applyStructuralChange(mergeStopInto(form.values.locations, sourceIndex, targetIndex));
  };

  /** Orders loaded before `locIndex`, i.e. the ones this stop is allowed to unload. */
  const getAllUnloadableOrders = (locIndex: number): GlobalOrdersRequest[] =>
    form.values.locations
      .slice(0, locIndex)
      .map(loc => loc.orders)
      .flat()
      .filter(t => t != null)
      .filter(t => isNotBlank(t.orderName));

  const unloadOrder = (locIndex: number, orderId: string) => {
    const lastLocationIndex = form.values.locations.length - 1;

    const previousLocation = form.values.locations.find(t => t.unloadingOrderIds?.includes(orderId));
    if (previousLocation) {
      const prevIndex = form.values.locations.indexOf(previousLocation);
      form.setFieldValue(
        `locations[${prevIndex}].unloadingOrderIds`,
        previousLocation.unloadingOrderIds.filter(t => t !== orderId),
      );
    }
    form.setFieldValue(
      `locations[${locIndex}].unloadingOrderIds`,
      Array.from(new Set([...(form.values.locations[locIndex].unloadingOrderIds || []), orderId])),
    );

    if (locIndex !== lastLocationIndex && form.values.locations[lastLocationIndex].unloadingOrderIds?.includes(orderId)) {
      form.setFieldValue(
        `locations[${lastLocationIndex}].unloadingOrderIds`,
        form.values.locations[lastLocationIndex].unloadingOrderIds.filter(t => t !== orderId),
      );
    }
  };

  /** Drops the unloading area of a stop, pushing everything it unloaded to the last stop. */
  const deleteUnloadingLocation = (locIndex: number) => {
    const lastLocationIndex = form.values.locations.length - 1;
    const carried = form.values.locations[locIndex].unloadingOrderIds || [];
    const lastUnloads = form.values.locations[lastLocationIndex].unloadingOrderIds || [];

    // Folding away an area that was holding nothing must not touch the form: a write here would
    // change the identity of `locations` and set every form-derived request going again.
    if (carried.length === 0) {
      if (form.values.locations[locIndex].unloadingOrderIds !== null) {
        form.setFieldValue(`locations[${locIndex}].unloadingOrderIds`, null);
      }
      return;
    }

    form.setFieldValue(`locations[${lastLocationIndex}].unloadingOrderIds`, Array.from(new Set([...lastUnloads, ...carried])), () => {
      form.setFieldValue(`locations[${locIndex}].unloadingOrderIds`, null);
    });
  };

  /**
   * A newly created order is unloaded at the last stop until the user says otherwise.
   *
   * Safe to call in the same handler that adds the order: `setFieldValue` updates functionally, so
   * it composes on top of the pending change, and the last stop's assignments are not affected by
   * loading an order anywhere else.
   */
  const syncOrdersToLastLocations = (sourceLocIndex: number, orderId: string) => {
    const lastIndex = form.values.locations.length - 1;
    const lastLocation = form.values.locations[lastIndex];
    const lastUnloads = lastLocation.unloadingOrderIds || [];

    if (!lastUnloads.includes(orderId)) {
      form.setFieldValue(`locations[${lastIndex}].unloadingOrderIds`, [...lastUnloads, orderId]);
    }
  };

  return {
    orderIssues,
    moveStop,
    moveOrderLoading,
    addStopAbove,
    addStopAtEnd,
    canDeleteLocation,
    deleteLocation,
    mergeStop,
    getAllUnloadableOrders,
    unloadOrder,
    deleteUnloadingLocation,
    syncOrdersToLastLocations,
  };
};